import {useWeatherForecastSource} from '../app/externalSources/weatherForecastSource.js';
import type {WeatherForecast} from '../lib/weather.js';

const CHIP = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 9,
  flex: '0 0 auto',
  // Reserved footprint so loading -> ready -> unavailable never reflows the
  // right cluster.
  minWidth: 104,
  justifyContent: 'center',
  padding: '6px 13px 6px 10px',
  borderRadius: 999,
  background: 'rgba(255,255,255,.12)',
  whiteSpace: 'nowrap' as const,
  fontFamily: 'inherit',
  lineHeight: 1,
};

const TEMP = {
  color: '#ffffff',
  fontSize: 15,
  fontWeight: 800,
  fontVariantNumeric: 'tabular-nums',
};

const DETAIL = {
  color: 'rgba(255,255,255,.75)',
  fontSize: 12,
  fontWeight: 600,
  fontVariantNumeric: 'tabular-nums',
};

type CloudIconProps = {
  size?: number;
};

type ForecastReading = {
  temp?: unknown;
  precipProb?: unknown;
};

type HeaderForecast = WeatherForecast & {
  current?: ForecastReading;
  today?: ForecastReading;
};

type WeatherStatus = 'loading' | 'ready' | 'unavailable';

type HeaderWeatherChipProps = {
  onOpen?: () => void;
};

// Inline stroke glyph on currentColor — the prototype's Unicode cloud is a
// stand-in and emoji are not shipped into production chrome.
function CloudIcon({size = 15}: CloudIconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M17.5 19a4.5 4.5 0 0 0 .5-8.97 6 6 0 0 0-11.66-1.4A4 4 0 0 0 6.5 19z" />
    </svg>
  );
}

function round(value: unknown): number | null {
  return value == null ? null : Math.round(value as number);
}

export default function HeaderWeatherChip({onOpen}: HeaderWeatherChipProps) {
  const {data: sourceForecast, status: sourceStatus} = useWeatherForecastSource();
  const forecast = sourceForecast as HeaderForecast | null;
  const temp = round(forecast?.current?.temp);
  const summary = temp == null ? null : {temp, precip: round(forecast?.today?.precipProb)};

  // A syntactically successful payload can still be unusable for the compact
  // chip (for example it might lack current.temp). Keep the chrome soft-fail
  // semantics even when the external source itself has a non-null payload.
  const status: WeatherStatus = summary ? 'ready' : sourceStatus === 'loading' ? 'loading' : 'unavailable';
  const ready = status === 'ready';
  const readySummary = ready ? summary : null;
  const label = `${
    readySummary
      ? `Current weather ${readySummary.temp} degrees${readySummary.precip == null ? '' : `, ${readySummary.precip} percent chance of rain`}`
      : 'Current weather unavailable'
  }. Opens Weather page.`;

  return (
    <button
      type="button"
      data-header-weather="1"
      data-header-weather-state={status}
      onClick={onOpen}
      style={{
        ...CHIP,
        color: ready ? 'rgba(255,255,255,.92)' : 'rgba(255,255,255,.55)',
        border: 'none',
        cursor: 'pointer',
        font: 'inherit',
      }}
      title={label}
      aria-label={label}
    >
      <CloudIcon size={15} />
      {readySummary ? (
        <>
          <span style={TEMP}>{readySummary.temp}&deg;</span>
          {/* Secondary detail. index.html drops it at the narrow breakpoint so
              the first header row stays deterministic on a 375px phone; the
              temperature and the aria-label keep the full reading. */}
          {readySummary.precip != null && (
            <span data-header-weather-detail="1" style={DETAIL}>
              Rain {readySummary.precip}%
            </span>
          )}
        </>
      ) : (
        <span style={{...TEMP, color: 'rgba(255,255,255,.55)'}}>&mdash;&deg;</span>
      )}
    </button>
  );
}
